import { getToken, clearToken } from '../utils/authToken';
/**
 * AppComments - Sistema de Comentarios para Apps
 * Permite a usuarios comentar, editar y eliminar sus comentarios
 */
import { useState, useEffect } from 'react';
import { 
  MessageSquare, Send, Edit3, Trash2, Loader2, 
  User, Calendar, Star, MoreVertical, X, Check
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';
import StarRating from './StarRating';

import { API_URL } from '../config';

// Format date to readable string
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diff = now - date;
  
  // Less than 1 minute
  if (diff < 60000) return 'Ahora mismo';
  // Less than 1 hour
  if (diff < 3600000) return `Hace ${Math.floor(diff / 60000)} min`;
  // Less than 24 hours
  if (diff < 86400000) return `Hace ${Math.floor(diff / 3600000)}h`;
  // Less than 7 days
  if (diff < 604800000) return `Hace ${Math.floor(diff / 86400000)}d`;
  
  return date.toLocaleDateString('es-ES', { 
    day: 'numeric', 
    month: 'short',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
};

// Single Comment Component
const CommentItem = ({ comment, currentUserId, onEdit, onDelete, isSpanish }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [saving, setSaving] = useState(false);
  
  const isOwner = currentUserId && comment.user_id === currentUserId;
  const wasEdited = comment.updated_at !== comment.created_at;
  
  const handleSaveEdit = async () => {
    if (!editContent.trim() || editContent.trim() === comment.content) {
      setIsEditing(false);
      setEditContent(comment.content);
      return;
    }
    
    setSaving(true);
    try {
      await onEdit(comment.id, editContent.trim());
      setIsEditing(false);
    } catch (error) {
      // Error handled in parent
    } finally {
      setSaving(false);
    }
  };
  
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent(comment.content);
  };
  
  return (
    <div className="group bg-zinc-800/50 rounded-xl p-4 border border-zinc-700/50 hover:border-zinc-600/50 transition-colors">
      {/* Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-3">
          {/* Avatar */}
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
            {comment.user_name?.charAt(0).toUpperCase() || 'U'}
          </div>
          
          {/* User Info */}
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-white">{comment.user_name}</span>
              {comment.user_rating > 0 && (
                <div className="flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-500/20 rounded-full">
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                  <span className="text-[10px] text-amber-400">{comment.user_rating}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 text-[10px] text-zinc-500">
              <span>{formatDate(comment.created_at)}</span>
              {wasEdited && (
                <span className="text-zinc-600">(editado)</span>
              )}
            </div>
          </div>
        </div>
        
        {/* Actions Menu */}
        {isOwner && !isEditing && (
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 hover:bg-zinc-700 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
            >
              <MoreVertical className="w-4 h-4 text-zinc-400" />
            </button>
            
            {showMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-8 w-32 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-20 py-1">
                  <button
                    onClick={() => { setIsEditing(true); setShowMenu(false); }}
                    className="w-full px-3 py-1.5 text-left text-xs text-zinc-300 hover:bg-zinc-700 flex items-center gap-2"
                  >
                    <Edit3 className="w-3 h-3" />
                    {isSpanish ? 'Editar' : 'Edit'}
                  </button>
                  <button
                    onClick={() => { onDelete(comment.id); setShowMenu(false); }}
                    className="w-full px-3 py-1.5 text-left text-xs text-red-400 hover:bg-red-900/30 flex items-center gap-2"
                  >
                    <Trash2 className="w-3 h-3" />
                    {isSpanish ? 'Eliminar' : 'Delete'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
      
      {/* Content */}
      {isEditing ? (
        <div className="mt-2">
          <textarea
            value={editContent}
            onChange={(e) => setEditContent(e.target.value.slice(0, 500))}
            className="w-full px-3 py-2 bg-zinc-900 border border-zinc-600 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-indigo-500 resize-none"
            rows={3}
            autoFocus
          />
          <div className="flex items-center justify-between mt-2">
            <span className="text-[10px] text-zinc-500">{editContent.length}/500</span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancelEdit}
                className="px-3 py-1 text-xs text-zinc-400 hover:text-white transition-colors"
              >
                {isSpanish ? 'Cancelar' : 'Cancel'}
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving || !editContent.trim()}
                className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs rounded-lg transition-colors flex items-center gap-1 disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                {isSpanish ? 'Guardar' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-sm text-zinc-300 whitespace-pre-wrap break-words">{comment.content}</p>
      )}
    </div>
  );
};

// Main Comments Component
const AppComments = ({ appId, isAuthenticated }) => {
  const { language } = useLanguage();
  const isSpanish = language === 'es';
  
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  
  // Get current user ID
  useEffect(() => {
    const fetchUserId = async () => {
      const token = getToken();
      if (token) {
        try {
          // Fetch user ID from /auth/me endpoint
          const response = await axios.get(`${API_URL}/api/auth/me`, {
          });
          if (response.data.id) {
            setCurrentUserId(response.data.id);
          }
        } catch (e) {
          // Fallback to token parsing if API fails
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            setCurrentUserId(payload.sub || payload.user_id);
          } catch (e) {
            // Invalid token
          }
        }
      }
    };
    fetchUserId();
  }, []);
  
  // Load comments
  useEffect(() => {
    if (appId) {
      loadComments(true);
    }
  }, [appId]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const loadComments = async (reset = false) => {
    if (reset) {
      setPage(0);
      setLoading(true);
    }
    
    try {
      const skip = reset ? 0 : page * 20;
      const response = await axios.get(`${API_URL}/api/published-apps/${appId}/comments?skip=${skip}&limit=20`);
      
      if (reset) {
        setComments(response.data.comments || []);
      } else {
        setComments(prev => [...prev, ...(response.data.comments || [])]);
      }
      setTotal(response.data.total || 0);
      setHasMore(response.data.has_more || false);
      if (!reset) setPage(prev => prev + 1);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !isAuthenticated) return;
    
    setSubmitting(true);
    try {
      const token = getToken();
      const response = await axios.post(
        `${API_URL}/api/published-apps/${appId}/comments`,
        { content: newComment.trim() },
        {}
      );
      
      // Add new comment at the beginning
      setComments(prev => [response.data.comment, ...prev]);
      setTotal(prev => prev + 1);
      setNewComment('');
      toast.success(isSpanish ? '¡Comentario publicado!' : 'Comment posted!');
    } catch (error) {
      toast.error(isSpanish ? 'Error al publicar comentario' : 'Error posting comment');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleEdit = async (commentId, content) => {
    const token = getToken();
    const response = await axios.put(
      `${API_URL}/api/published-apps/comments/${commentId}`,
      { content },
      {}
    );
    
    // Update comment in list
    setComments(prev => prev.map(c => 
      c.id === commentId ? response.data.comment : c
    ));
    toast.success(isSpanish ? 'Comentario actualizado' : 'Comment updated');
  };
  
  const handleDelete = async (commentId) => {
    if (!window.confirm(isSpanish ? '¿Eliminar este comentario?' : 'Delete this comment?')) return;
    
    try {
      const token = getToken();
      await axios.delete(
        `${API_URL}/api/published-apps/comments/${commentId}`,
        {}
      );
      
      setComments(prev => prev.filter(c => c.id !== commentId));
      setTotal(prev => prev - 1);
      toast.success(isSpanish ? 'Comentario eliminado' : 'Comment deleted');
    } catch (error) {
      toast.error(isSpanish ? 'Error al eliminar' : 'Error deleting comment');
    }
  };
  
  return (
    <div className="border-t border-zinc-700 bg-zinc-900/50">
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-indigo-400" />
        <h3 className="text-sm font-semibold text-white">
          {isSpanish ? 'Comentarios' : 'Comments'}
        </h3>
        <span className="text-xs text-zinc-500">({total})</span>
      </div>
      
      {/* Comment Form */}
      <form onSubmit={handleSubmit} className="p-4 border-b border-zinc-800">
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value.slice(0, 500))}
          placeholder={isSpanish ? 'Escribe tu comentario...' : 'Write your comment...'}
          className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-indigo-500 resize-none"
          rows={3}
          disabled={!isAuthenticated}
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-[10px] text-zinc-500">{newComment.length}/500</span>
          <button
            type="submit"
            disabled={!isAuthenticated || submitting || !newComment.trim()}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-zinc-700 disabled:text-zinc-500 text-white text-xs rounded-lg transition-colors flex items-center gap-2"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
            {!isAuthenticated 
              ? (isSpanish ? 'Inicia sesión para comentar' : 'Login to comment')
              : (isSpanish ? 'Publicar comentario' : 'Post comment')
            }
          </button>
        </div>
      </form>
      
      {/* Comments List */}
      <div className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-indigo-400 animate-spin" />
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8">
            <MessageSquare className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">
              {isSpanish ? 'No hay comentarios aún' : 'No comments yet'}
            </p>
            <p className="text-xs text-zinc-600 mt-1">
              {isSpanish ? '¡Sé el primero en comentar!' : 'Be the first to comment!'}
            </p>
          </div>
        ) : (
          <>
            {comments.map(comment => (
              <CommentItem
                key={comment.id}
                comment={comment}
                currentUserId={currentUserId}
                onEdit={handleEdit}
                onDelete={handleDelete}
                isSpanish={isSpanish}
              />
            ))}
            
            {/* Load More */}
            {hasMore && (
              <button
                onClick={() => loadComments(false)}
                className="w-full py-2 text-xs text-indigo-400 hover:text-indigo-300 transition-colors"
              >
                {isSpanish ? 'Cargar más comentarios' : 'Load more comments'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AppComments;
